import { boundForPlanner } from "./tools";
import type { ToolResult } from "./tools";

export type WireToolCall = { id: string; name: string; arguments: string };

export type WireMessage = {
  role: string;
  content?: string | null;
  toolCalls?: WireToolCall[] | null;
  toolCallId?: string | null;
};

const SKIPPED_RESULT: ToolResult = {
  ok: false,
  deviationCode: "SKIPPED",
  message: "Hoppet over — turen ble avsluttet før verktøyet ble kjørt.",
};

// The single source of the SKIPPED tool reply used both by the live loop's early-break balancing and
// by toResumableConversation when it aborts a dangling call on a hydrated conversation.
export const skippedToolReply = (toolCallId: string): WireMessage => ({
  role: "tool",
  toolCallId,
  content: boundForPlanner(SKIPPED_RESULT),
});

// Owns the assistant/tool message array and the balance invariant it upholds: an assistant message
// that lists tool calls must be followed by exactly one role:"tool" reply per call before the next
// plan request — the real result, USER_DECLINED on cancel, or a SKIPPED placeholder on an early
// break — or the planner rejects the next turn with a 400. pushSkippedRemainder keeps that balancing
// in one named place so the rest of the loop never touches the raw array.
export const createAgentMemory = (messages: WireMessage[]) => ({
  initSystem: (content: string): void => {
    if (messages.length === 0) {
      messages.push({ role: "system", content });
      return;
    }
    const first = messages[0];
    if (first.role === "system" && first.content !== content) {
      first.content = content;
    }
  },
  pushUser: (content: string): void => {
    messages.push({ role: "user", content });
  },
  pushAssistantWithToolCalls: (content: string | null, toolCalls: WireToolCall[]): void => {
    messages.push({ role: "assistant", content, toolCalls });
  },
  pushToolResult: (toolCallId: string, result: ToolResult): void => {
    messages.push({ role: "tool", toolCallId, content: boundForPlanner(result) });
  },
  pushSkippedRemainder: (toolCalls: WireToolCall[], fromIndex: number): void => {
    for (let i = fromIndex; i < toolCalls.length; i++) {
      messages.push(skippedToolReply(toolCalls[i].id));
    }
  },
  pushAssistantReply: (content: string): void => {
    messages.push({ role: "assistant", content });
  },
  // The planner may have already pushed an assistant reply this turn; the streamed answer replaces
  // that trailing message rather than appending a second assistant turn.
  replaceTrailingAssistantReply: (content: string): void => {
    messages[messages.length - 1] = { role: "assistant", content };
  },
});

export type AgentMemory = ReturnType<typeof createAgentMemory>;
